import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Play, Square, RotateCw, Copy, Eye, EyeOff, RefreshCw, Loader2, KeyRound, HardDriveDownload, AlertTriangle, ArrowUpRight } from "lucide-react";
import UpgradePlanDialog from "@/components/UpgradePlanDialog";

export default function VpsDetail() {
  const { vpsId } = useParams();
  const [vps, setVps] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pwDialog, setPwDialog] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [reinstDialog, setReinstDialog] = useState(false);
  const [osTemplates, setOsTemplates] = useState([]);
  const [reinstOs, setReinstOs] = useState("");
  const [reinstPw, setReinstPw] = useState("");
  const [reinstConfirm, setReinstConfirm] = useState("");
  const [reinstalling, setReinstalling] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(
    () => api.get(`/vps/${vpsId}`).then((r) => setVps(r.data)),
    [vpsId],
  );
  useEffect(() => { load(); }, [load]);
  const provider = vps?.provider;
  const ipv4 = vps?.ipv4;
  const status = vps?.status;
  const provisionState = vps?.provision_state;
  const maintenanceState = vps?.maintenance_state;

  // Auto-poll while IP is missing OR still provisioning (checa a cada 8s)
  useEffect(() => {
    if (!provider) return;
    const needsSync = provider === "brasil"
      ? !["ready", "failed", "cancelled"].includes(provisionState) ||
        ["reinstalling", "changing_password"].includes(maintenanceState)
      : !ipv4 || status === "provisioning";
    if (!needsSync) { if (pollRef.current) clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.post(`/vps/${vpsId}/sync`);
        setVps(data);
        if (data.ipv4 && data.status !== "provisioning") clearInterval(pollRef.current);
      } catch {/* keep polling silently */}
    }, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [provider, ipv4, status, provisionState, maintenanceState, vpsId]);

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vps/${vpsId}/action`, { action });
      setVps(data);
      toast.success(`VPS ${action === "start" ? "iniciada" : action === "stop" ? "parada" : "reiniciada"}.`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/vps/${vpsId}/sync`);
      setVps(data);
      toast.success(data.ipv4 ? "Dados atualizados." : "Ainda instalando — tente novamente em 30s.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSyncing(false); }
  };

  const genRandomPw = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let s = "";
    for (let i = 0; i < 16; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setNewPw(s);
  };

  const savePassword = async () => {
    if (newPw.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    setSavingPw(true);
    try {
      await api.post(`/vps/${vpsId}/password`, { root_password: newPw });
      await load();
      toast.success("Senha alterada — a VPS pode reiniciar em alguns segundos.");
      setPwDialog(false);
      setNewPw("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingPw(false); }
  };

  const copy = (text, what) => { navigator.clipboard.writeText(text); toast.success(`${what} copiado`); };

  const genRandomPwFor = (setter) => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let s = ""; for (let i = 0; i < 16; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setter(s);
  };

  const openReinstall = async () => {
    setReinstConfirm("");
    genRandomPwFor(setReinstPw);
    setReinstDialog(true);
    // Carrega OS templates do backend (lista curada known-good)
    try {
      const { data } = await api.get("/os-templates", { params: { vps_id: vpsId } });
      const list = Array.isArray(data) ? data : [];
      setOsTemplates(list);
      // Pré-seleciona o OS atual da VPS, se estiver na lista; senão o primeiro
      const current = list.find((o) => o.slug === vps.os);
      setReinstOs(current ? current.slug : (list[0]?.slug || ""));
    } catch (e) {
      toast.error("Falha ao carregar lista de sistemas: " + formatApiError(e));
    }
  };

  const submitReinstall = async () => {
    if (!reinstOs) { toast.error("Escolha um sistema operacional"); return; }
    if (reinstPw.length < 8) { toast.error("Senha mínima de 8 caracteres"); return; }
    if (reinstConfirm !== "REINSTALAR") { toast.error("Digite REINSTALAR para confirmar"); return; }
    setReinstalling(true);
    try {
      await api.post(`/vps/${vpsId}/reinstall`, { os_template: reinstOs, root_password: reinstPw });
      toast.success("Reinstalação iniciada. A VPS voltará ao ar após o novo acesso SSH ser validado.");
      setReinstDialog(false);
      setReinstConfirm("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setReinstalling(false); }
  };

  if (!vps) return <div className="text-zinc-500">Carregando…</div>;

  const isRunning = vps.status === "running";
  const noIp = !vps.ipv4 && vps.provider !== "brasil";
  const retryFailedReinstall = vps.provider === "brasil" &&
    vps.provision_state === "ready" &&
    vps.maintenance_state === "failed" &&
    ["running", "stopped", "provisioning"].includes(vps.status);
  const brPending = vps.provider === "brasil" &&
    (vps.provision_state !== "ready" || ["reinstalling", "failed"].includes(vps.maintenance_state));
  const maintenanceBusy = ["reinstalling", "changing_password"].includes(vps.maintenance_state);

  return (
    <div className="space-y-8" data-testid="vps-detail-page">
      <Link to="/dashboard/vps" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// vps details</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">{vps.hostname}</h1>
          <div className="mt-2 flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 font-mono text-sm ${isRunning ? "text-emerald-400" : vps.status === "provisioning" ? "text-amber-400" : "text-red-400"}`}>
              <span className={`h-2 w-2 rounded-full ${isRunning ? "bg-emerald-500 dot-blink" : vps.status === "provisioning" ? "bg-amber-500 dot-blink" : "bg-red-500"}`} />
              {vps.status.toUpperCase()}
            </span>
            <span className="text-xs font-mono text-zinc-500">criado em {fmtDate(vps.created_at)}</span>
          </div>
          <Button
            onClick={() => setUpgradeOpen(true)}
            className="mt-3 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="vps-upgrade-open"
          >
            <ArrowUpRight className="mr-2 h-4 w-4" /> Fazer upgrade desta VPS
          </Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={sync} disabled={syncing} variant="outline" className="rounded-sm border-white/20 hover:bg-white hover:text-black" data-testid="vps-sync-btn" title="Atualizar dados do portal Flix">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Atualizando…" : "Atualizar"}
          </Button>
          <Button onClick={() => act("start")} disabled={busy || isRunning || maintenanceBusy || (brPending && !retryFailedReinstall)} variant="outline" className="rounded-sm border-white/20 hover:bg-emerald-500 hover:text-black hover:border-emerald-500" data-testid="vps-start-btn">
            <Play className="h-4 w-4 mr-2" /> Iniciar
          </Button>
          <Button onClick={() => act("stop")} disabled={busy || !isRunning || maintenanceBusy} variant="outline" className="rounded-sm border-white/20 hover:bg-red-500 hover:text-black hover:border-red-500" data-testid="vps-stop-btn">
            <Square className="h-4 w-4 mr-2" /> Parar
          </Button>
          <Button onClick={() => act("restart")} disabled={busy || !isRunning || maintenanceBusy} variant="outline" className="rounded-sm border-white/20 hover:bg-white hover:text-black" data-testid="vps-restart-btn">
            <RotateCw className="h-4 w-4 mr-2" /> Reiniciar
          </Button>
          <Button onClick={openReinstall} disabled={busy || maintenanceBusy || (brPending && !retryFailedReinstall) || (vps.status === "provisioning" && !retryFailedReinstall) || vps.status === "suspended" || vps.status === "terminated" || (vps.manual_provisioning && !vps.ipv4)} variant="outline" className="rounded-sm border-white/20 hover:bg-amber-500 hover:text-black hover:border-amber-500" data-testid="vps-reinstall-btn" title="Reinstalar sistema operacional (todos os dados serão apagados)">
            <HardDriveDownload className="h-4 w-4 mr-2" /> Reinstalar SO
          </Button>
        </div>
      </div>

      {noIp && (
        <div className="border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3 font-mono text-sm text-amber-300" data-testid="vps-provisioning-banner">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Aguardando o portal Flix atribuir o IP. O processo leva de 30s a 3min — a página atualiza sozinha.</span>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-white/10 bg-[#121214] p-6" data-testid="vps-access-box">
          {brPending ? (
            <div className="space-y-3">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// entrega Brasil</div>
              <div className="font-heading text-2xl font-bold">
                {vps.maintenance_state === "reinstalling" ? "Reinstalando o sistema…" :
                  vps.maintenance_state === "failed" ? "Reinstalação precisa de assistência" :
                  vps.provision_state === "waiting_payment" ? "Aguardando confirmação do PIX" :
                  vps.provision_state === "failed" ? "Entrega precisa de assistência" :
                  vps.provision_state === "cancelled" ? "Pagamento cancelado" : "Preparando sua VPS…"}
              </div>
              <p className="text-sm text-zinc-400">
                  {vps.maintenance_state === "failed"
                    ? "A reinstalação anterior não foi concluída. O IP permanece reservado; a senha só volta a aparecer após uma reinstalação bem-sucedida. Você pode tentar novamente abaixo."
                    : vps.provision_state === "failed"
                    ? "A equipe foi avisada. Seus dados de acesso aparecerão aqui somente após a VPS estar acessível. Abra um chamado se precisar de ajuda."
                  : "IP e senha serão exibidos aqui após o pagamento e a confirmação de acesso à VPS."}
              </p>
                {vps.ipv4 && ["reinstalling", "failed"].includes(vps.maintenance_state) && (
                  <div className="flex items-center gap-2 border border-white/10 bg-[#09090B] p-3 font-mono text-sm" data-testid="vps-maintenance-ip">
                    <span className="text-zinc-400">IP reservado:</span>
                    <span className="text-white">{vps.ipv4}</span>
                    <button
                      onClick={() => copy(vps.ipv4, "IP")}
                      className="text-zinc-500 hover:text-white"
                      data-testid="vps-maintenance-ip-copy"
                      aria-label="Copiar IP da VPS"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
            </div>
          ) : vps.manual_provisioning && (!vps.ipv4 || !vps.root_password) ? (
            <div className="space-y-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// entrega em preparação</div>
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-pink-500 dot-blink" />
                <div className="font-heading text-2xl font-bold">Criando a VPS…</div>
              </div>
              <div className="border border-pink-500/40 bg-pink-500/5 p-4 rounded-sm">
                <div className="text-sm text-pink-200 font-mono">
                  Sua VPS está sendo preparada pela nossa equipe.
                </div>
                <div className="text-xs text-zinc-400 mt-2 leading-relaxed">
                  ⏱️ <span className="text-white">Entrega em até 60 minutos</span> após a confirmação do pagamento.
                  Assim que o servidor estiver pronto, você receberá o IP e a senha
                  root aqui no painel e por email.
                </div>
              </div>
              <div className="text-xs font-mono text-zinc-500">
                Enquanto isso, sinta-se à vontade para abrir um ticket de suporte se tiver alguma dúvida.
              </div>
            </div>
          ) : (
            <>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">// como conectar via ssh</div>
          <div className="space-y-3 font-mono text-sm">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-zinc-500">host</span>
              <span className="flex items-center gap-2">
                {vps.ipv4 || <span className="text-zinc-600">aguardando…</span>}
                {vps.ipv4 && <button onClick={() => copy(vps.ipv4, "IP")}><Copy className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /></button>}
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-zinc-500">porta</span>
              <span className="flex items-center gap-2">
                22
                <button onClick={() => copy("22", "Porta")}><Copy className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /></button>
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-zinc-500">user</span>
              <span className="flex items-center gap-2">
                root
                <button onClick={() => copy("root", "Usuário")}><Copy className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /></button>
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-zinc-500">password</span>
              <span className="flex items-center gap-2">
                {showPass ? vps.root_password : "•".repeat(16)}
                <button onClick={() => setShowPass((v) => !v)}>{showPass ? <EyeOff className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /> : <Eye className="h-3.5 w-3.5 text-zinc-500 hover:text-white" />}</button>
                <button onClick={() => copy(vps.root_password, "Senha")}><Copy className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /></button>
                <button disabled={maintenanceBusy || brPending || !isRunning} onClick={() => { genRandomPw(); setPwDialog(true); }} title="Trocar senha" data-testid="vps-change-pass-icon" className="disabled:opacity-30 disabled:cursor-not-allowed"><KeyRound className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /></button>
              </span>
            </div>
            <div className="mt-4 border border-white/10 p-4 bg-[#09090B] text-xs space-y-3">
              <div>
                <div className="text-zinc-500 mb-1">Linux / macOS (Terminal):</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-emerald-300">{vps.ipv4 ? `ssh root@${vps.ipv4}` : "ssh root@... (aguardando IP)"}</code>
                  {vps.ipv4 && <button onClick={() => copy(`ssh root@${vps.ipv4}`, "Comando SSH")}><Copy className="h-3.5 w-3.5 text-zinc-500 hover:text-white" /></button>}
                </div>
              </div>
              <div className="border-t border-white/10 pt-3">
                <div className="text-zinc-500 mb-1">Windows (PuTTY):</div>
                <ol className="list-decimal list-inside text-zinc-400 space-y-0.5">
                  <li>Baixe o PuTTY em <a className="text-emerald-300 underline hover:text-emerald-200" href="https://www.putty.org/" target="_blank" rel="noreferrer">putty.org</a></li>
                  <li>Host Name: <code className="text-emerald-300">{vps.ipv4 || "..."}</code> · Port: <code className="text-emerald-300">22</code> · Connection type: SSH</li>
                  <li>Login como <code className="text-emerald-300">root</code> e cole a senha acima</li>
                </ol>
              </div>
              {noIp && (
                <div className="border-t border-white/10 pt-3 text-amber-300">
                  ⚠️ A VPS ainda está sendo provisionada. O SSH só fica acessível após o status virar <b>RUNNING</b> — normalmente entre 2 e 5 minutos após a compra.
                </div>
              )}
              {vps.ipv4 && isRunning && (
                <div className="border-t border-white/10 pt-3 text-emerald-300">
                  ✓ VPS ativa. Se receber "Connection timed out" na primeira tentativa, aguarde ~60s (SSH pode levar um instante extra para iniciar após o boot).
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>

        <div className="border border-white/10 bg-[#121214] p-6 space-y-4" data-testid="vps-specs-box">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// specs</div>
          <ul className="space-y-2 font-mono text-sm">
            <li className="flex justify-between"><span className="text-zinc-500">CPU</span><span>{vps.cpu_cores} vCPU</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">RAM</span><span>{vps.ram_gb} GB</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">SSD</span><span>{vps.ssd_gb} GB NVMe</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">Tráfego</span><span>{vps.bandwidth_tb ? `${vps.bandwidth_tb} TB` : "Ilimitado"}</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">Porta</span><span>1 Gbps dedicado</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">Anti-DDoS</span><span>Incluso</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">Uptime SLA</span><span>99,9%</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">SO</span><span>{vps.os}</span></li>
            <li className="flex justify-between"><span className="text-zinc-500">Local</span><span>{vps.location}</span></li>
            <li className="flex justify-between border-t border-white/10 pt-2"><span className="text-zinc-500">Expira</span><span>{fmtDate(vps.expires_at)}</span></li>
          </ul>
        </div>
      </div>

      <UpgradePlanDialog
        vpsId={vpsId}
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
      />

      <Dialog open={pwDialog} onOpenChange={(v) => { if (!v) { setPwDialog(false); setNewPw(""); } }}>
        <DialogContent className="rounded-sm bg-[#121214] border-white/10 max-w-md" data-testid="vps-pw-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tighter flex items-center gap-2"><KeyRound className="h-5 w-5" /> Trocar senha root</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-400">{vps.provider === "brasil" ? "A nova senha será aplicada e validada com um login SSH antes de ser salva no painel." : "A nova senha será aplicada na VPS pelo portal Flix. A VM pode reiniciar por alguns segundos."}</p>
            <div className="flex gap-2">
              <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nova senha (mín. 8)" className="rounded-sm bg-[#09090B] border-white/10 font-mono" data-testid="vps-pw-input" />
              <Button type="button" variant="outline" onClick={genRandomPw} className="rounded-sm border-white/20 shrink-0" data-testid="vps-pw-gen">Gerar</Button>
            </div>
            <Button onClick={savePassword} disabled={savingPw || newPw.length < 8} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200" data-testid="vps-pw-save">
              {savingPw ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Aplicando…</>) : "Trocar senha"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reinstDialog} onOpenChange={(v) => { if (!v) { setReinstDialog(false); setReinstConfirm(""); } }}>
        <DialogContent className="rounded-sm bg-[#121214] border-white/10 max-w-lg" data-testid="vps-reinstall-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
              <HardDriveDownload className="h-5 w-5" /> Reinstalar Sistema Operacional
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2 text-red-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold uppercase tracking-wide text-xs mb-1">Atenção — Ação destrutiva</div>
                <div className="text-red-200/90">Todos os dados, arquivos, senhas e configurações da VPS <b>{vps.hostname}</b> serão apagados. A VM ficará offline por 2–5 minutos e voltará com o sistema escolhido abaixo.</div>
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Sistema Operacional</label>
              <select
                value={reinstOs}
                onChange={(e) => setReinstOs(e.target.value)}
                className="w-full rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white"
                data-testid="vps-reinstall-os"
              >
                {osTemplates.length === 0 && <option value="">Carregando…</option>}
                {osTemplates.map((o) => (
                  <option key={o.slug} value={o.slug}>{o.name} ({o.slug})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Nova senha root</label>
              <div className="flex gap-2">
                <Input value={reinstPw} onChange={(e) => setReinstPw(e.target.value)} placeholder="mín. 8 caracteres" className="rounded-sm bg-[#09090B] border-white/10 font-mono" data-testid="vps-reinstall-pw" />
                <Button type="button" variant="outline" onClick={() => genRandomPwFor(setReinstPw)} className="rounded-sm border-white/20 shrink-0" data-testid="vps-reinstall-pw-gen">Gerar</Button>
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Digite <b className="text-red-300">REINSTALAR</b> para confirmar</label>
              <Input value={reinstConfirm} onChange={(e) => setReinstConfirm(e.target.value.toUpperCase())} placeholder="REINSTALAR" className="rounded-sm bg-[#09090B] border-white/10 font-mono uppercase tracking-widest" data-testid="vps-reinstall-confirm" />
            </div>

            <Button
              onClick={submitReinstall}
              disabled={reinstalling || reinstConfirm !== "REINSTALAR" || reinstPw.length < 8 || !reinstOs}
              className="w-full rounded-sm bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              data-testid="vps-reinstall-submit"
            >
              {reinstalling ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reinstalando…</>) : "Confirmar reinstalação"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
